import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BranchJiraResolution, JiraIssueSuggestionDTO } from '@clearflow/shared';
import type { PipelineStatusResponse } from '@clearflow/shared';
import { api } from '../../lib/api.ts';
import { useClampedMenuPosition } from './useClampedMenuPosition.ts';
import { pipelineBadgeClass, StageIcon, type BranchInfo } from './GitGraph.tsx';

export interface BranchMenuPosition extends BranchInfo {
  x: number;
  y: number;
}

const PIPELINE_IN_PROGRESS = new Set([
  'running', 'pending', 'created', 'waiting_for_resource', 'preparing', 'scheduled',
]);

// Même recette que les icônes de CommitContextMenu.tsx (trait currentColor, viewBox 16, épaisseur
// 1.4) — pas d'emoji couleur ici pour rester homogène avec le reste des menus contextuels.
function LinkIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.6 9.4 L9.4 6.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M7.3 4.4 L8.2 3.5a2.2 2.2 0 0 1 3.1 3.1l-0.9 0.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8.7 11.6 L7.8 12.5a2.2 2.2 0 0 1-3.1-3.1l0.9-0.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// Même paire de crochets que LinkIcon, plus une diagonale de coupe — pour rester visuellement
// apparentée (même relation) plutôt qu'un pictogramme sans rapport (ciseaux, croix...).
function UnlinkIcon() {
  return (
    <svg className="ccm-icon-svg" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.3 4.4 L8.2 3.5a2.2 2.2 0 0 1 3.1 3.1l-0.9 0.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8.7 11.6 L7.8 12.5a2.2 2.2 0 0 1-3.1-3.1l0.9-0.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M3 3 L13 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// Supprimer la branche par défaut ou une branche protégée est presque toujours une erreur
// (et GitLab la refuse de toute façon pour la branche par défaut) — on bloque côté client
// avec un message clair plutôt que de laisser l'utilisateur découvrir un 400/403 générique.
function computeDeleteBlockedReason(position: BranchInfo, canDeleteBranch: boolean): string | null {
  if (position.isDefault) return 'Impossible de supprimer la branche par défaut';
  if (position.isProtected) return 'Cette branche est protégée — retire la protection sur GitLab avant de la supprimer';
  if (!canDeleteBranch) return 'Ton rôle sur ce dépôt (Guest/Reporter) ne permet pas de supprimer une branche';
  return null;
}

// Même recette que computeDeleteBlockedReason ci-dessus, pour les deux actions Jira — grisées avec
// un message plutôt que masquées (voir aussi computeUnlinkBlockedReason), pour qu'on comprenne
// toujours pourquoi une action du menu est indisponible sans avoir à deviner l'état de la branche.
function computeLinkBlockedReason(jiraLoading: boolean, ticket: BranchJiraResolution | undefined): string | null {
  if (jiraLoading || !ticket?.ticketKey) return null;
  if (ticket.source === 'manual') return `Déjà lié manuellement à ${ticket.ticketKey} — utilise le crayon pour le changer`;
  return `Déjà résolu automatiquement vers ${ticket.ticketKey} depuis le nom de branche`;
}

function computeUnlinkBlockedReason(jiraLoading: boolean, ticket: BranchJiraResolution | undefined): string | null {
  if (jiraLoading) return null;
  if (!ticket?.ticketKey) return 'Aucun ticket lié à cette branche';
  if (ticket.source === 'manual') return null;
  return 'Résolu automatiquement depuis le nom de branche — rien à délier';
}

// Extrait de BranchContextMenu (S3776 : complexité cognitive) — bloc autonome, ne dépend que de
// la réponse de pipelineQuery.
function BranchPipelineSection({ pipeline }: { pipeline: PipelineStatusResponse | undefined }) {
  if (!pipeline) return null;

  if (pipeline.stages.length > 0) {
    const stageIcons = pipeline.stages.map((s) => <StageIcon key={s.name} name={s.name} status={s.status} />);
    return (
      <div className="branch-tooltip-pipeline">
        <span className="branch-tooltip-pipeline-label">Pipeline</span>
        {pipeline.id != null && <span className="branch-tooltip-pipeline-id">#{pipeline.id}</span>}
        {pipeline.webUrl ? (
          <a className="branch-tooltip-stages" href={pipeline.webUrl} target="_blank" rel="noreferrer">
            {stageIcons}
          </a>
        ) : (
          <span className="branch-tooltip-stages">{stageIcons}</span>
        )}
      </div>
    );
  }

  if (!pipeline.status) return null;
  return (
    <div className="branch-tooltip-pipeline">
      <span className="branch-tooltip-pipeline-label">Pipeline</span>
      {pipeline.id != null && <span className="branch-tooltip-pipeline-id">#{pipeline.id}</span>}
      <span className={`branch-tooltip-badge ${pipelineBadgeClass(pipeline.status)}`}>{pipeline.status}</span>
    </div>
  );
}

interface BranchJiraLinkPickerProps {
  query: string;
  onChangeQuery: (value: string) => void;
  results: JiraIssueSuggestionDTO[] | undefined;
  isLoading: boolean;
  isLinking: boolean;
  isLinkError: boolean;
  onPick: (ticketKey: string) => void;
  onCancel: () => void;
}

// Extrait de BranchContextMenu (S3776) — même mécanique de picker que JiraAssigneeField
// (JiraTicketTooltip.tsx) : vide / chargement / aucun résultat / liste de résultats.
function BranchJiraLinkPicker({
  query, onChangeQuery, results, isLoading, isLinking, isLinkError, onPick, onCancel,
}: BranchJiraLinkPickerProps) {
  return (
    <div className="branch-tooltip-body">
      <div className="jira-tooltip-editor">
        <input
          autoFocus
          className="jira-tooltip-editor-input"
          placeholder="Rechercher un ticket…"
          value={query}
          onChange={(e) => onChangeQuery(e.target.value)}
        />
        <div className="jira-tooltip-editor-list">
          {query.length === 0 ? (
            <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Tapez pour rechercher…</span>
          ) : isLoading ? (
            <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Recherche…</span>
          ) : results?.length === 0 ? (
            <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Aucun résultat</span>
          ) : (
            results?.map((issue) => (
              <button key={issue.key} type="button" disabled={isLinking} onClick={() => onPick(issue.key)}>
                <strong style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{issue.key}</strong>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {issue.summary}
                </span>
              </button>
            ))
          )}
        </div>
        {isLinkError && <span className="jira-tooltip-editor-error">Échec de la liaison</span>}
        <button type="button" className="jira-tooltip-editor-cancel" onClick={onCancel}>✕</button>
      </div>
    </div>
  );
}

interface Props {
  repoId: string;
  position: BranchMenuPosition;
  onClose: () => void;
  onDeleteBranch: () => void;
  isDeletingBranch: boolean;
  deleteBranchError: string | null;
  canDeleteBranch: boolean;
}

// Ouvert au clic (comme CommitContextMenu), pas au survol : un menu doit rester lisible pendant
// qu'on clique sur ses liens, pas disparaître dès que le curseur quitte la ligne de branche.
export function BranchContextMenu({
  repoId,
  position,
  onClose,
  onDeleteBranch,
  isDeletingBranch,
  deleteBranchError,
  canDeleteBranch,
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  // freeze = linking : une fois le picker de tickets ouvert, chaque frappe redimensionne
  // .jira-tooltip-editor-list (résultats qui apparaissent/disparaissent) — sans ça, le
  // ResizeObserver du hook reclampe à chaque frappe et peut faire basculer tout le menu de
  // l'autre côté du point de clic, l'éloignant du curseur en pleine recherche (voir le
  // commentaire de useClampedMenuPosition).
  const { left, top } = useClampedMenuPosition(ref, position.x, position.y, linking);
  const queryClient = useQueryClient();

  const deleteBlockedReason = computeDeleteBlockedReason(position, canDeleteBranch);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const jiraQuery = useQuery({
    queryKey: ['jira-branch', repoId, position.name],
    queryFn: () => api.resolveBranchTicket(repoId, position.name),
    staleTime: 60_000,
  });

  const ticket = jiraQuery.data;
  const linkBlockedReason = computeLinkBlockedReason(jiraQuery.isLoading, ticket);
  const unlinkBlockedReason = computeUnlinkBlockedReason(jiraQuery.isLoading, ticket);

  // Suggestions du picker — seulement une fois l'éditeur ouvert ET au moins un caractère tapé,
  // même raison que assignableUsersQuery dans JiraTicketTooltip.tsx : éviter un appel Jira par
  // simple ouverture du menu.
  const issuePickerQuery = useQuery({
    queryKey: ['jira-issue-picker', linkQuery],
    queryFn: () => api.issuePicker(linkQuery),
    enabled: linking && linkQuery.length > 0,
    staleTime: 10_000,
  });

  const linkMutation = useMutation({
    mutationFn: (ticketKey: string) => api.linkBranch({ repoId, branchName: position.name, ticketKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jira-branch', repoId, position.name] });
      // Préfixe seul (pas repoId/branchNames complets) : invalide toute variante de la query de
      // la sidebar "Tickets Jira" (RepoViewPage.tsx), même principe que invalidateAfterEdit dans
      // JiraTicketTooltip.tsx — un lien tout juste créé peut faire apparaître ce ticket là-bas.
      queryClient.invalidateQueries({ queryKey: ['jira-repo-tickets'] });
      setLinking(false);
      setLinkQuery('');
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: () => api.unlinkBranch(repoId, position.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jira-branch', repoId, position.name] });
      queryClient.invalidateQueries({ queryKey: ['jira-repo-tickets'] });
      setLinking(false);
      setLinkQuery('');
    },
  });

  const pipelineQuery = useQuery({
    queryKey: ['pipeline-status', repoId, position.name],
    queryFn: () => api.pipelineStatus(repoId, position.name),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && PIPELINE_IN_PROGRESS.has(status) ? 5_000 : false;
    },
  });

  return (
    <div ref={ref} className="commit-context-menu" style={{ left, top }}>
      <div className="ccm-header">
        <span className="branch-tooltip-dot" style={{ background: position.color }} />
        <span className="branch-tooltip-name" style={{ color: position.color }}>
          {position.name}
        </span>
        {position.originSha && (
          <code className="branch-tooltip-sha">&#8624; {position.originSha.slice(0, 8)}</code>
        )}
        {position.isDefault && <span className="branch-tooltip-badge badge-default">default</span>}
        {position.isProtected && <span className="branch-tooltip-badge badge-protected">protected</span>}
        {position.isMerged && <span className="branch-tooltip-badge badge-merged">merged</span>}
      </div>
      <div className="ccm-separator" />
      <BranchPipelineSection pipeline={pipelineQuery.data} />
      {jiraQuery.isLoading && (
        <div className="branch-tooltip-body">
          <span className="branch-tooltip-jira-loading">Jira…</span>
        </div>
      )}
      {!jiraQuery.isLoading && linking && (
        <BranchJiraLinkPicker
          query={linkQuery}
          onChangeQuery={setLinkQuery}
          results={issuePickerQuery.data}
          isLoading={issuePickerQuery.isLoading}
          isLinking={linkMutation.isPending}
          isLinkError={linkMutation.isError}
          onPick={(ticketKey) => linkMutation.mutate(ticketKey)}
          onCancel={() => { setLinking(false); setLinkQuery(''); }}
        />
      )}
      {!jiraQuery.isLoading && !linking && ticket?.ticketKey && (
        <div className="branch-tooltip-body">
          <div className="branch-tooltip-jira">
            {ticket.ticket?.url ? (
              <a
                className="branch-tooltip-jira-key"
                href={ticket.ticket.url}
                target="_blank"
                rel="noreferrer"
              >
                {ticket.ticketKey}
              </a>
            ) : (
              <span className="branch-tooltip-jira-key">{ticket.ticketKey}</span>
            )}
            {ticket.ticket?.status && (
              <span>{ticket.ticket.status}</span>
            )}
            {ticket.ticket?.assignee && (
              <span className="branch-tooltip-jira-assignee">
                {ticket.ticket.assignee.displayName}
              </span>
            )}
            {/* Uniquement pour un lien manuel — un ticket résolu par regex depuis le nom de
                branche (source 'branch-name') n'a pas de lien à changer, juste à créer un
                premier lien manuel qui prendrait le dessus (voir resolveRepoTickets/jira.ts:
                priorité manualByBranch > extractTicketKey). */}
            {ticket.source === 'manual' && (
              <button
                type="button"
                className="branch-tooltip-jira-edit"
                title="Changer le ticket lié"
                onClick={() => setLinking(true)}
              >
                ✎
              </button>
            )}
          </div>
        </div>
      )}
      <div className="ccm-separator" />
      {/* Grisées avec un message plutôt que masquées (voir linkBlockedReason/unlinkBlockedReason
          ci-dessus) — même traitement que "Supprimer la branche" plus bas : l'utilisateur voit
          toujours que ces actions existent et comprend pourquoi elles sont indisponibles. */}
      <button
        type="button"
        className="ccm-item"
        onClick={() => setLinking(true)}
        disabled={jiraQuery.isLoading || linking || !!linkBlockedReason}
        title={linkBlockedReason ?? undefined}
      >
        <LinkIcon />
        <span>Lier un ticket Jira</span>
      </button>
      <button
        type="button"
        className="ccm-item"
        onClick={() => unlinkMutation.mutate()}
        disabled={jiraQuery.isLoading || unlinkMutation.isPending || !!unlinkBlockedReason}
        title={unlinkBlockedReason ?? undefined}
      >
        <UnlinkIcon />
        <span>{unlinkMutation.isPending ? 'Suppression du lien…' : 'Délier ce ticket'}</span>
      </button>
      {unlinkMutation.isError && (
        <div style={{ padding: '2px 14px 4px' }}>
          <span className="badge error">Erreur : échec de la suppression du lien</span>
        </div>
      )}
      <div className="ccm-separator" />
      {deleteBranchError ? (
        <div style={{ padding: '6px 14px' }}>
          <span className="badge error">Erreur : {deleteBranchError}</span>
        </div>
      ) : confirmingDelete ? (
        <button className="ccm-item" onClick={onDeleteBranch} disabled={isDeletingBranch}>
          <span className="ccm-icon">⚠</span>
          <span>{isDeletingBranch ? 'Suppression…' : 'Confirmer la suppression ?'}</span>
        </button>
      ) : (
        <button
          className="ccm-item"
          onClick={() => setConfirmingDelete(true)}
          disabled={!!deleteBlockedReason}
          title={deleteBlockedReason ?? undefined}
        >
          <span className="ccm-icon">🗑</span>
          <span>Supprimer la branche</span>
        </button>
      )}
    </div>
  );
}
